// Verträge für Standorte (Lager, Filiale, Fahrzeug, virtuelle Standorte) und ihre Lagerplätze.
import { defineContract, defineEvent } from '@shared/contract'
import { zId } from '@shared/ids'
import { listInput, listOutput } from '@shared/list'
import { z } from 'zod'
import { BIN_KINDS, LOCATION_KINDS } from '../lib/labels'
import { zById, zExpectedVersion, zOptionalId, zOptionalText, zSaved, zText, zTimestamp, zToggle } from './common'

const zCode = (max: number, example: string) =>
  z
    .string()
    .trim()
    .toUpperCase()
    .min(1)
    .max(max)
    .regex(/^[A-Z0-9][A-Z0-9._-]*$/, { error: `Bitte nur Buchstaben, Ziffern, Punkt und Bindestrich verwenden (z. B. ${example}).` })

const zLocationRef = z.object({ id: zId, code: z.string(), name: z.string() })

export const zLocationRow = z.object({
  id: zId,
  version: z.int(),
  code: z.string(),
  name: z.string(),
  kind: z.enum(LOCATION_KINDS),
  isVirtual: z.boolean(),
  homeLocation: zLocationRef.nullable(),
  supplyLocation: zLocationRef.nullable(),
  allowNegativeStock: z.boolean(),
  isEstablishment: z.boolean(),
  salesAreaM2: z.int().nullable(),
  street: z.string().nullable(),
  zip: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  sort: z.int(),
  active: z.boolean(),
  binCount: z.int(),
  customFields: z.record(z.string(), z.unknown()),
  updatedAt: zTimestamp,
})

const locationFields = {
  name: zText(120),
  /** Fahrzeug: Standort, an dem es steht und beladen wird */
  homeLocationId: zOptionalId,
  /** Filiale: Standort, der sie mit Ware versorgt (Nachschub) */
  supplyLocationId: zOptionalId,
  /** Kassenabgänge dürfen den Bestand ins Minus führen (nur Lager, Filiale, Fahrzeug) */
  allowNegativeStock: z.boolean(),
  /** Betriebsstätte im steuerlichen Sinn */
  isEstablishment: z.boolean(),
  /** Verkaufsfläche in m² (200-m²-Regel der Einweg-Rücknahme) */
  salesAreaM2: z.int().min(0).max(1_000_000).nullish(),
  street: zOptionalText(160),
  zip: zOptionalText(12),
  city: zOptionalText(120),
  country: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{2}$/, { error: 'Bitte das Land als zweistelliges Kürzel angeben (DE, AT …).' })
    .nullish(),
  phone: zOptionalText(60),
  email: z.email({ error: 'Bitte eine gültige E-Mail-Adresse eingeben.' }).max(200).nullish(),
  sort: z.int().min(0).max(100_000),
  customFields: z.record(z.string(), z.unknown()).optional(),
}

export const locationList = defineContract({
  name: 'masterdata.location.list',
  title: 'Standorte anzeigen',
  description: 'Listet die Standorte, für die das Leserecht gilt: Lager, Filialen, Fahrzeuge und virtuelle Standorte.',
  llm: {
    description:
      'Listet Standorte (Lager, Filialen, Fahrzeuge sowie virtuelle Standorte wie den Transit) mit Kürzel, Art, ' +
      'lieferndem Standort und Status. Verwenden, um eine Standort-ID zu einem Namen oder Kürzel zu finden.',
    keywords: ['Standort', 'Filiale', 'Lager', 'Zentrallager', 'Fahrzeug', 'Markt', 'Niederlassung'],
  },
  input: listInput({
    sortBy: ['sort', 'code', 'name'],
    defaultSort: { by: 'sort' },
    filters: {
      kind: z.enum(LOCATION_KINDS).optional(),
      active: z.boolean().optional(),
      /** true: nur Lager, Filialen und Fahrzeuge */
      physical: z.boolean().optional(),
    },
  }),
  output: listOutput(zLocationRow),
  permission: 'masterdata.location.read',
  risk: 'read',
  scope: 'any',
})

export const locationGet = defineContract({
  name: 'masterdata.location.get',
  title: 'Standort öffnen',
  description: 'Zeigt einen Standort mit Anschrift und Einstellungen.',
  llm: {
    description: 'Liefert einen Standort mit Anschrift, Art, lieferndem Standort und Einstellungen. Lagerplätze: masterdata.bin.list.',
    keywords: ['Standort', 'Filiale', 'Lager', 'Anschrift'],
  },
  input: zById,
  output: zLocationRow,
  permission: 'masterdata.location.read',
  risk: 'read',
  scope: (input) => [input.id],
})

export const locationCreate = defineContract({
  name: 'masterdata.location.create',
  title: 'Standort anlegen',
  description: 'Legt einen Standort an. Der Standardplatz entsteht von selbst.',
  llm: {
    description:
      'Legt einen neuen Standort an: Lager, Filiale, Fahrzeug – oder einen virtuellen Standort (etwa den ' +
      'Transit). Die Art ist danach fest. Ein Fahrzeug braucht einen Heimatstandort; bei einer Filiale nennt ' +
      '`supplyLocationId` das Lager, das sie versorgt.',
    keywords: ['Standort anlegen', 'neue Filiale', 'neues Lager', 'Fahrzeug anlegen'],
  },
  input: z.strictObject({
    /** kurzes Kürzel für Listen und Etiketten: ZL, F01, LKW-3 */
    code: zCode(12, 'F04'),
    kind: z.enum(LOCATION_KINDS),
    ...locationFields,
    allowNegativeStock: locationFields.allowNegativeStock.default(false),
    isEstablishment: locationFields.isEstablishment.optional(),
    sort: locationFields.sort.optional(),
  }),
  output: zSaved,
  permission: 'masterdata.location.write',
  risk: 'write',
})

export const locationUpdate = defineContract({
  name: 'masterdata.location.update',
  title: 'Standort ändern',
  description: 'Ändert Name, Kürzel, Anschrift und Einstellungen eines Standorts. Die Art bleibt.',
  llm: {
    description: 'Ändert einen Standort – nur die mitgegebenen Felder. Die Art (Lager, Filiale …) lässt sich nicht ändern.',
    keywords: ['Standort ändern', 'Filiale umbenennen', 'Anschrift ändern'],
  },
  input: z.strictObject({
    id: zId,
    expectedVersion: zExpectedVersion,
    code: zCode(12, 'F04').optional(),
    ...locationFields,
    name: locationFields.name.optional(),
    allowNegativeStock: locationFields.allowNegativeStock.optional(),
    isEstablishment: locationFields.isEstablishment.optional(),
    sort: locationFields.sort.optional(),
  }),
  output: zSaved,
  permission: 'masterdata.location.write',
  risk: 'write',
})

export const locationDeactivate = defineContract({
  name: 'masterdata.location.deactivate',
  title: 'Standort stilllegen',
  description: 'Legt einen Standort still. Das geht nur ohne Bestand; Belege und Bewegungen bleiben erhalten.',
  llm: {
    description:
      'Legt einen Standort still (Filiale geschlossen, Fahrzeug abgegeben). Voraussetzung: kein Bestand mehr am ' +
      'Standort und kein anderer aktiver Standort wird von ihm versorgt. Gelöscht wird nichts.',
    keywords: ['Standort schließen', 'Filiale schließen', 'stilllegen', 'deaktivieren'],
  },
  input: zToggle,
  output: zSaved,
  permission: 'masterdata.location.write',
  risk: 'destructive',
  effects: { reversible: true },
})

export const locationReactivate = defineContract({
  name: 'masterdata.location.reactivate',
  title: 'Standort wieder in Betrieb nehmen',
  description: 'Macht einen stillgelegten Standort wieder nutzbar.',
  llm: { description: 'Gegenstück zu masterdata.location.deactivate.', keywords: ['Standort reaktivieren', 'wieder öffnen'] },
  input: zToggle,
  output: zSaved,
  permission: 'masterdata.location.write',
  risk: 'write',
  effects: { reversible: true },
})

// ---- Lagerplätze ----------------------------------------------------------------------------------------------

export const zBinRow = z.object({
  id: zId,
  version: z.int(),
  locationId: zId,
  code: z.string(),
  name: z.string().nullable(),
  kind: z.enum(BIN_KINDS),
  /** false = Sperrplatz: Bestand dort ist nicht verfügbar */
  isAvailable: z.boolean(),
  isDefault: z.boolean(),
  sort: z.int(),
  active: z.boolean(),
  /** auf dem Platz liegt Bestand */
  hasStock: z.boolean(),
  /** Bereich, Gang, Regal, Fach bzw. Ebene – frei, nur beschreibend (Migration 1002) */
  zone: z.string().nullable().default(null),
  aisle: z.string().nullable().default(null),
  rack: z.string().nullable().default(null),
  shelf: z.string().nullable().default(null),
  /** Zugriffssperre: kein Vorschlag, keine Entnahme, kein Umbuchen auf den Platz (Bestand bleibt) */
  locked: z.boolean().default(false),
  lockReason: z.string().nullable().default(null),
})

/** Bereich, Gang, Regal, Fach – frei und optional (Lagerplätze, docs/kommissionieren/SPEZIFIKATION.md K1) */
const zBinStructure = {
  zone: zOptionalText(40),
  aisle: zOptionalText(40),
  rack: zOptionalText(40),
  shelf: zOptionalText(40),
}

export const binList = defineContract({
  name: 'masterdata.bin.list',
  title: 'Lagerplätze anzeigen',
  description: 'Listet die Lagerplätze eines Standorts.',
  llm: {
    description:
      'Listet die Lagerplätze eines Standorts mit Art (Greifplatz, Reserve, Wareneingang, Sperrplatz und weitere), ' +
      'Standardplatz und ob Bestand darauf liegt.',
    keywords: ['Lagerplatz', 'Regal', 'Stellplatz', 'Sperrplatz', 'Gang'],
  },
  input: listInput({
    sortBy: ['sort', 'code'],
    defaultSort: { by: 'sort' },
    filters: { locationId: zId, kind: z.enum(BIN_KINDS).optional(), active: z.boolean().optional(), zone: z.string().trim().max(40).optional() },
  }),
  output: listOutput(zBinRow),
  permission: 'masterdata.location.read',
  risk: 'read',
  scope: (input) => [input.locationId],
})

export const binCreate = defineContract({
  name: 'masterdata.bin.create',
  title: 'Lagerplatz anlegen',
  description: 'Legt einen Lagerplatz an einem Standort an.',
  llm: {
    description:
      'Legt einen Lagerplatz an. Die Art „Sperrplatz“ macht den Bestand darauf nicht verfügbar – das lässt sich später ' +
      'nicht mehr ändern (dann neuen Platz anlegen und umlagern). `sort` ist die Reihenfolge im Laufweg beim ' +
      'Kommissionieren; Bereich, Gang, Regal und Fach beschreiben den Platz. Ohne die Funktion „Lagerplätze“ nur ' +
      'Sperrplätze und Plätze für Rückgut.',
    keywords: ['Lagerplatz anlegen', 'Regal', 'Stellplatz', 'Sperrplatz'],
  },
  input: z.strictObject({
    locationId: zId,
    code: zCode(24, 'A-01-03'),
    name: zOptionalText(80),
    kind: z.enum(BIN_KINDS).default('pick'),
    isDefault: z.boolean().default(false),
    sort: z.int().min(0).max(100_000).optional(),
    ...zBinStructure,
  }),
  output: zSaved,
  permission: 'masterdata.location.bins',
  risk: 'write',
  scope: (input) => [input.locationId],
})

export const binUpdate = defineContract({
  name: 'masterdata.bin.update',
  title: 'Lagerplatz ändern',
  description: 'Ändert Kürzel, Name, Art oder Reihenfolge eines Lagerplatzes oder macht ihn zum Standardplatz.',
  llm: {
    description:
      'Ändert einen Lagerplatz. Ein Platz wird nicht nachträglich zum Sperrplatz und ein Sperrplatz nicht wieder ' +
      'verfügbar. `isDefault: true` macht ihn zum Standardplatz des Standorts. `locked: true` mit `lockReason` sperrt ' +
      'den Platz für das Kommissionieren und Umbuchen dorthin (Regal beschädigt, Umbau) – der Bestand bleibt; ' +
      '`locked: false` gibt ihn wieder frei. Standard- und Sperrplätze lassen sich nicht sperren.',
    keywords: ['Lagerplatz ändern', 'umbenennen', 'Standardplatz', 'Lagerplatz sperren', 'Platz freigeben', 'Laufweg'],
  },
  input: z.strictObject({
    id: zId,
    expectedVersion: zExpectedVersion,
    code: zCode(24, 'A-01-03').optional(),
    name: zOptionalText(80),
    kind: z.enum(BIN_KINDS).optional(),
    isDefault: z.literal(true).optional(),
    sort: z.int().min(0).max(100_000).optional(),
    ...zBinStructure,
    locked: z.boolean().optional(),
    lockReason: zOptionalText(200),
  }),
  output: zSaved,
  permission: 'masterdata.location.bins',
  risk: 'write',
  scope: 'record',
})

export const binDeactivate = defineContract({
  name: 'masterdata.bin.deactivate',
  title: 'Lagerplatz stilllegen',
  description: 'Legt einen Lagerplatz still. Das geht nur ohne Bestand und nicht für den Standardplatz.',
  llm: {
    description: 'Legt einen leeren Lagerplatz still. Der Standardplatz eines Standorts bleibt immer aktiv.',
    keywords: ['Lagerplatz stilllegen', 'Lagerplatz löschen', 'Regal auflösen'],
  },
  input: zToggle,
  output: zSaved,
  permission: 'masterdata.location.bins',
  risk: 'destructive',
  scope: 'record',
  effects: { reversible: true },
})

export const binReactivate = defineContract({
  name: 'masterdata.bin.reactivate',
  title: 'Lagerplatz wieder nutzen',
  description: 'Macht einen stillgelegten Lagerplatz wieder nutzbar.',
  llm: { description: 'Gegenstück zu masterdata.bin.deactivate.', keywords: ['Lagerplatz reaktivieren'] },
  input: zToggle,
  output: zSaved,
  permission: 'masterdata.location.bins',
  risk: 'write',
  scope: 'record',
  effects: { reversible: true },
})

/** Ein Standort wurde angelegt, geändert, stillgelegt oder wieder in Betrieb genommen. */
export const locationChanged = defineEvent({
  name: 'masterdata.location.changed',
  title: 'Standort geändert',
  payload: z.object({ id: zId, code: z.string(), change: z.enum(['created', 'updated', 'deactivated', 'reactivated']) }),
})
